/**
 * Màn "Lịch hẹn" — màn lễ tân mở suốt ngày.
 *
 * Quy tắc (skill `miniapp-ux-vn`, docs/ui/THAM-KHAO.md §3.3):
 *   - Tablet: lịch theo **cột kỹ thuật viên**, 1 giờ = 112px, khối tô theo **trạng thái** (viền trái
 *     đậm + nền nhạt), vạch đỏ "bây giờ". Chạm một khối → tờ chi tiết, nút chính là bước kế tiếp.
 *   - Điện thoại: danh sách theo giờ, mỗi dòng **một** nút chính. "Không đến" / "Huỷ lịch" nằm
 *     trong tờ chi tiết và phải xác nhận — vòng 0 của `ui-critic` bắt hai nút đó đứng sát nút
 *     chính, cùng kiểu, dễ bấm nhầm.
 *   - Không kéo-thả trên điện thoại (docs/NOT-DOING.md #13).
 */

import { formatVnTime, nowVn } from '@sapa/shared'
import {
  CalendarClock,
  CalendarDays,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Hash,
  MessageSquareText,
  MoreHorizontal,
  Phone,
  Plus,
  Scissors,
  UserRound,
  UserRoundPlus,
  Users,
  Wallet,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  type Api,
  ApiFailure,
  type Branch,
  buocKe,
  type DayAppointment,
  type DayBoard as DayBoardData,
  gioPhut,
  hienSdt,
  ngayVn,
  type ServiceOption,
  type ToiLaAi,
  tenDichVu,
  tenKhach,
  themNgay,
  tienVnd,
} from './api.ts'
import { BookSheet } from './BookSheet.tsx'
import {
  hanToastMs,
  kieuToast,
  type ToastQuay,
  toastSauDoi,
  toastSauHuy,
  toastSauTuChoi,
} from './bao-khach.ts'
import { type BoTaiLich, type Dich, type LoiTai, taoBoTaiLich } from './bo-tai-lich.ts'
import { TheCanNhac } from './CanNhac.tsx'
import type { TheCauHinh } from './CauHinh.tsx'
import { ChuanBiTiem } from './ChuanBiTiem.tsx'
import { MucCoc, tinhViecCoc } from './Coc.tsx'
import { useTinhNang } from './cai-dat-tiem.ts'
import { donKhiDoiChiNhanh } from './chi-nhanh-dang-dung.ts'
import { DongTenTiem, useChiNhanh } from './chi-nhanh-dang-dung.tsx'
import { ToDoiGio } from './DoiGio.tsx'
import { layLichMo, thanDoiGio, thanHoanTac, type VuaDoi } from './doi-lich.ts'
import { OLyDo, ToGoiViKhach, ToHoanTat } from './GoiVi.tsx'
import { HoSoBuoi } from './HoSoBuoi.tsx'
import { ToKhachVao } from './KhachVao.tsx'
import {
  bangRong,
  chuNhanTre,
  duocBamKhongDen,
  khungLuoi,
  laTre,
  lichCuNhatChuaDong,
  themCotChoTho,
} from './man-dau.ts'
import { ToNghiDotXuat } from './NghiDotXuat.tsx'
import { nhanNgayDayDu, nhanNgayNgan, phutBayGio } from './ngay.ts'
import { cauDaiLichVuong, nhanVienDoiGio, nutNghiDotXuatHien } from './nghi-dot-xuat.ts'
import { NutTinhTien, ToTinhTien } from './TinhTien.tsx'
import { G } from './text/goi-vi.ts'
import { hanhDongCua, T, TRANG_THAI } from './text/vi.ts'
import { batTuTaiLai } from './tu-phuc-hoi.ts'
import {
  Avatar,
  CacCum,
  DauTrangThaiLich,
  moBangChonNgay,
  Rong,
  ThongBao,
  To,
  useLanTaiMoiNhat,
  Xuong,
} from './ui.tsx'

/**
 * 1 giờ = 112px (vòng 44; trước 96px): chữ trong khối lịch lên 16px (CLAUDE.md §7) nên mỗi dòng cao ~22px —
 * khối 30 phút cao 53px đủ một dòng tên + giờ, 45 phút đủ tên + giờ + dịch vụ (bẻ tối đa 2 dòng). Phải khớp
 * `--gio-px` trong styles.css (vạch giờ).
 */
const PX_GIO = 112
const PX_PHUT = PX_GIO / 60
/** Độ cao khối (px) để hiện thêm dòng: dịch vụ · dịch vụ bẻ 2 dòng · trạng thái + SĐT · giá. Mỗi dòng 16px ≈ 22px. */
const CAO_DONG_2 = 54
const CAO_DONG_2_BE = 76
const CAO_DONG_3 = 100
const CAO_DONG_4 = 124

interface Props {
  toi: ToiLaAi
  api: Api
  onMoCauHinh: (the: TheCauHinh) => void
  /** Mở thẳng một ngày (từ danh sách lịch hẹn: "Xem trên lịch"). */
  ngayDau?: string
  /** BK-2: mở luôn tờ "Xử lý" khi dữ liệu ngày tải xong (từ cảnh báo "lịch vướng" ở Cấu hình). */
  moXuLyDau?: boolean
  /** Đã mở tờ "Xử lý" theo `moXuLyDau` — nơi giữ trạng thái xoá cờ để quay lại Lịch không mở lại. */
  onDaMoXuLyDau?: () => void
  /** Từ chuông: mở luôn tờ chi tiết của lịch này khi bảng ngày tải xong (lịch đã huỷ / không còn → chỉ hiện ngày, không báo lỗi). */
  lichDau?: string
  /** Mở sẵn tờ đặt lịch với số này (từ trang Khách). */
  sdtDau?: string
  /** Vừa mở tiệm xong: thẻ "Chuẩn bị tiệm" giữ lời chào ở đầu thẻ tới khi chủ bấm một bước hoặc đóng. */
  chaoMoTiem?: boolean
  onDongChao?: () => void
  /** Chỉ CHỦ tiệm có: nút "Khai tài khoản nhận tiền" ở tờ thu khi tiệm chưa có mã QR (v0.23.2) → mở Cấu hình → Cài đặt tiệm. */
  onKhaiTaiKhoan?: () => void
}

export function TrangLich({
  toi,
  api,
  onMoCauHinh,
  ngayDau,
  moXuLyDau = false,
  onDaMoXuLyDau,
  lichDau,
  sdtDau,
  chaoMoTiem = false,
  onDongChao,
  onKhaiTaiKhoan,
}: Props) {
  /*
   * Đặt lịch hộ khách là việc của quầy, không phải của kỹ thuật viên. Phải kiểm ở đây chứ không
   * chỉ ẩn nút: màn này tải sẵn danh sách dịch vụ, mà KTV gọi đường đó sẽ nhận 403 → cả màn hỏng.
   */
  const choDatLich = toi.quyen.includes('dat-lich')
  const choCauHinh = toi.quyen.includes('cau-hinh.tiem')
  /** Lễ tân / quản lý / chủ: "Xong" đi qua tờ hoàn tất (trừ gói, trừ ví). KTV vẫn bấm thẳng. */
  const thuTien = toi.quyen.includes('thu-tien')
  /**
   * KH-GĐ2 7.1 (ADR-078): chủ tiệm bật "Thợ tự nhận khách và thu tiền" → thợ có `khach-vao` + `thu-tien.lich` (chỉ lịch
   * của mình). Ba vai quầy luôn có cả hai. Lịch chung nhiều thợ: thợ bấm "Xong" như cũ, quầy thu.
   */
  const khachVao = toi.quyen.includes('khach-vao')
  const laTho = toi.toi.vai === 'technician'
  const tinhTienDuoc = (a: DayAppointment) =>
    toi.quyen.includes('thu-tien.lich') && !(laTho && a.nhieuTho === true)
  const [hoanTatCho, setHoanTatCho] = useState<DayAppointment | null>(null)
  const [goiViCho, setGoiViCho] = useState<string | null>(null)
  const [branches, setBranches] = useState<Branch[] | null>(null)
  /** ADR-101: chi nhánh đang dùng chung cho cả máy (BK-2 đặt chi nhánh của lịch vướng qua `doi(…, {nho:false})`). */
  const { id: branchId, nhanDs: nhanDsChiNhanh } = useChiNhanh()
  const [services, setServices] = useState<ServiceOption[]>([])
  const [date, setDate] = useState(ngayDau ?? ngayVn())
  const [data, setData] = useState<DayBoardData | null>(null)
  /** Lỗi của VIỆC VỪA LÀM (dời lịch, đổi trạng thái…): người dùng đóng được. */
  const [loi, setLoi] = useState<string | null>(null)
  /** Lỗi của VIỆC TẢI (chi nhánh / lịch): tự gỡ khi tải được; không đóng tay. */
  const [loiTai, setLoiTai] = useState<LoiTai | null>(null)
  /** "hh:mm" giờ VN của lần tải lịch thành công gần nhất — để biết số đang xem là của lúc nào (đợt 3 · 3.1). */
  const [capNhatLuc, setCapNhatLuc] = useState<string | null>(null)
  const [chon, setChon] = useState<DayAppointment | null>(null)
  const [moDat, setMoDat] = useState(sdtDau !== undefined && toi.quyen.includes('dat-lich'))
  const [moKhachVao, setMoKhachVao] = useState(false)
  /** KH-GĐ2 4.2.4: tờ "Tính tiền cho ai?" (nút luôn thấy ở đầu màn). */
  const [moTinhTien, setMoTinhTien] = useState(false)
  /** Mở tờ đặt lịch từ một ô trống: đúng KTV, đúng giờ đã chạm. */
  const [dienSan, setDienSan] = useState<{
    staffId: string
    tenKtv: string
    startMin: number
  } | null>(null)
  /**
   * Lịch vừa dời — giữ vài giây để "Hoàn tác" dời ngược về chỗ cũ. Lưu NGÀY gốc và chỗ vừa dời tới: hoàn tác về đúng
   * ngày gốc (không phải ngày màn đang xem) và máy chủ từ chối nếu ai đó đã dời tiếp (N3 vòng sửa 3.1).
   */
  const [vuaDoi, setVuaDoi] = useState<VuaDoi | null>(null)
  /** Tờ "Đổi giờ" (điện thoại, và cả tablet qua tờ chi tiết). */
  const [doiGioCho, setDoiGioCho] = useState<DayAppointment | null>(null)
  /** Đợt 3 · 3.4: tờ nghỉ đột xuất — mở ở bước chọn người (nút) hoặc thẳng danh sách lịch cần xử lý (dải đỏ). */
  const [moNghi, setMoNghi] = useState<'chon' | 'ds' | null>(null)
  // BK-2: tới từ cảnh báo "lịch vướng" ở Cấu hình → mở luôn tờ "Xử lý" ngay khi bảng ngày tải xong (một lần).
  const daMoXuLyDau = useRef(false)
  useEffect(() => {
    if (!moXuLyDau || daMoXuLyDau.current || data === null || branchId === '' || !choDatLich) return
    daMoXuLyDau.current = true
    setMoNghi('ds')
    onDaMoXuLyDau?.()
  }, [moXuLyDau, data, branchId, choDatLich, onDaMoXuLyDau])
  // K9: nút "Xem" ở dải "ngày trước chưa thu" nhảy sang ngày của lịch CŨ NHẤT; tờ chi tiết của lịch đó mở ngay khi bảng ngày ấy tải xong.
  // Chuông: `lichDau` cũng đi đường này (cùng khoá `key` dựng lại màn nên giá trị đầu là đủ).
  const moSauKhiTai = useRef<string | null>(lichDau ?? null)
  useEffect(() => {
    const id = moSauKhiTai.current
    if (id === null || data === null || data.date !== date) return
    moSauKhiTai.current = null
    const a = data.appointments.find((x) => x.appointmentId === id)
    if (a !== undefined) setChon(a)
  }, [data, date])
  const [bao, setBao] = useState<ToastQuay | null>(null)
  /** Lịch vừa bấm "Khách đã tới" — giữ 6 giây để lễ tân "Hoàn tác" nếu bấm nhầm. */
  const [vuaToi, setVuaToi] = useState<DayAppointment | null>(null)
  /** Thẻ "Chuẩn bị tiệm" đang hiện: hộp "Chưa có lịch nào" ẩn đi (khỏi ba nút "Đặt lịch" cùng lúc). */
  // Mặc định "đang hiện" cho tới khi thẻ tải xong: tiệm chưa có lịch nào thì thẻ luôn hiện (bước 4 chưa xong).
  const [theChuanBiHien, setTheChuanBiHien] = useState(true)

  const lanMoi = useLanTaiMoiNhat()
  const boTai = useRef<BoTaiLich | null>(null)
  /**
   * Đích (chi nhánh, ngày) HIỆN TẠI của màn. Bộ tải đọc ref này ở mỗi lần tải và lúc ghi, nên hàm của lần vẽ cũ
   * (closure của `doiLich`, vòng thử lại) không bao giờ kéo lịch ngày / chi nhánh khác vào bảng (B1 vòng sửa 3.1).
   */
  const dichRef = useRef<Dich>({ branchId, date })
  dichRef.current = { branchId, date }
  // Bộ tải sống theo màn: chi nhánh lỗi thì tự thử lại, lịch lỗi thì giữ số đang xem (đợt 3 · 3.1).
  useEffect(() => {
    const bo = taoBoTaiLich({
      api,
      dich: () => dichRef.current,
      ngauNhien: Math.random,
      canDichVu: choDatLich || khachVao,
      lanMoi,
      gio: () => formatVnTime(nowVn()),
      phanUng: {
        chiNhanhXong: (b, s) => {
          setBranches(b)
          setServices(s)
          // Báo về nguồn chung (không tự lấy chi nhánh đầu): tiệm nhiều chi nhánh chưa chọn thì thẻ hỏi lo.
          nhanDsChiNhanh(b)
        },
        lichXong: (d, luc) => {
          setData(d)
          setCapNhatLuc(luc)
        },
        loi: setLoiTai,
      },
    })
    boTai.current = bo
    void bo.taiChiNhanh()
    // Bộ tải được dựng lại (đổi quyền / api) khi đã có đích: tải ngay, không đợi nhịp 60 giây.
    void bo.nap()
    return () => {
      bo.huy()
      boTai.current = null
    }
  }, [api, choDatLich, khachVao, lanMoi, nhanDsChiNhanh])

  /**
   * Tải lại lịch của đích HIỆN TẠI. Hàm này KHÔNG xoá lỗi của việc vừa làm (`loi`): trước đây `nap()` gọi
   * `setLoi(null)` nên lỗi dời lịch hiện rồi mất ngay vì `finally nap()` (V2 vòng sửa 3.1).
   */
  const nap = useCallback(async () => {
    await boTai.current?.nap()
  }, [])

  // Đổi ngày / chi nhánh: bỏ số của ngày cũ, bỏ lỗi + toast "Hoàn tác" của ngày cũ (không gửi đi nhầm ngày), rồi tải.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `branchId` / `date` là TÍN HIỆU chạy lại (đích đọc từ ref).
  useEffect(() => {
    setData(null)
    setCapNhatLuc(null)
    setLoi(null)
    setVuaDoi(null)
    setVuaToi(null)
    void nap()
  }, [branchId, date, nap])

  // Đổi chi nhánh giữa chừng (hiếm: chi nhánh đang dùng vừa bị ẩn, BK-2): đóng mọi tờ đang mở để không tờ nào ghi vào chi nhánh
  // khác với cái nó được mở cho. KHÔNG đụng khoá chống lặp (nằm ở từng tờ — bài học ADR-100). Lần đầu có chi nhánh ('' → id) thì bỏ qua.
  const chiNhanhTruoc = useRef(branchId)
  useEffect(() => {
    const truoc = chiNhanhTruoc.current
    chiNhanhTruoc.current = branchId
    if (truoc === '' || truoc === branchId) return
    const dong = donKhiDoiChiNhanh()
    setChon(dong.chon)
    setDienSan(dong.dienSan)
    setGoiViCho(dong.goiViCho)
    setDoiGioCho(dong.doiGioCho)
    setHoanTatCho(dong.hoanTatCho)
    setMoDat(dong.moDat)
    setMoKhachVao(dong.moKhachVao)
    setMoTinhTien(dong.moTinhTien)
    setMoNghi(dong.moNghi)
  }, [branchId])

  // Có mạng trở lại · tab hiện lại · mỗi 60 giây khi đang hiện: tải lại êm (không nháy, không đóng tờ đang mở).
  useEffect(() => batTuTaiLai(() => void boTai.current?.taiLaiTuDong()), [])

  /** Nút "Tải lại" ở dải báo lỗi — thay cho kéo-để-tải (đã tắt có chủ ý 23/09). */
  function taiLaiTay() {
    const bo = boTai.current
    if (bo === null) return
    setLoi(null)
    void (bo.chiNhanhDaCo() ? nap() : bo.taiChiNhanh())
  }

  useEffect(() => {
    if (bao === null) return
    // 6 giây (K1a); việc chủ tiệm còn phải làm tiếp (gọi báo khách) giữ 10 giây.
    const t = setTimeout(() => setBao(null), hanToastMs(bao.baoKhach, 6000))
    return () => clearTimeout(t)
  }, [bao])

  useEffect(() => {
    if (vuaToi === null) return
    const t = setTimeout(() => setVuaToi(null), 6000)
    return () => clearTimeout(t)
  }, [vuaToi])

  useEffect(() => {
    if (vuaDoi === null) return
    const t = setTimeout(() => setVuaDoi(null), hanToastMs(vuaDoi.baoKhach, 8000))
    return () => clearTimeout(t)
  }, [vuaDoi])

  /** Dời lịch — dùng chung cho kéo-thả (tablet) và tờ "Đổi giờ". Lỗi của máy chủ nói thẳng ra. */
  async function doiLich(a: DayAppointment, staffId: string, startMin: number, ngay = date) {
    if (a.staffId === null) return
    const ngayXem = date
    try {
      // Mang theo chỗ GỐC mà người bấm nhìn thấy (người · ngày · giờ): máy khác đã đổi thì máy chủ trả 409.
      const kq = await api.doiGio(
        a.appointmentId,
        thanDoiGio(a, ngayXem, { date: ngay, staffId, startMin }),
      )
      // Chỉ nhận toast "Hoàn tác" nếu người dùng chưa sang ngày / chi nhánh khác trong lúc chờ.
      if (dichRef.current.date === ngayXem) {
        setVuaDoi({
          a,
          tu: { date: ngayXem, staffId: a.staffId, startMin: a.startMin },
          den: { date: ngay, staffId, startMin },
          baoKhach: kq.baoKhach,
        })
      }
      setLoi(null)
    } catch (e) {
      setLoi(e instanceof ApiFailure ? e.messageVi : T.moveFailed)
    } finally {
      await nap()
    }
  }

  async function hoanTacDoi() {
    if (vuaDoi === null) return
    const v = vuaDoi
    setVuaDoi(null)
    try {
      await api.doiGio(v.a.appointmentId, thanHoanTac(v))
    } catch (e) {
      setLoi(e instanceof ApiFailure ? e.messageVi : T.moveFailed)
    } finally {
      await nap()
    }
  }

  async function hoanTac(a: DayAppointment) {
    setVuaToi(null)
    try {
      await api.setStatus(a.appointmentId, 'booked')
      void nap()
    } catch (e) {
      setLoi(e instanceof ApiFailure ? e.messageVi : T.statusFailed)
    }
  }

  const tenChiNhanh = branches?.find((b) => b.id === branchId)?.name ?? ''
  /** K9: lịch ngày trước còn dở — chỉ khi xem HÔM NAY (máy chủ chỉ trả cho hôm nay, vai tính được tiền lịch). */
  const lichChuaDong = date === ngayVn() ? (data?.chuaDongTruoc ?? []) : []
  function xemLichChuaDong() {
    const cu = lichCuNhatChuaDong(lichChuaDong)
    if (cu === null) return
    moSauKhiTai.current = cu.appointmentId
    setDate(cu.ngay)
  }
  const daiLichVuong =
    data === null ? null : cauDaiLichVuong(data.nghi ?? [], date, ngayVn(), data.ngoaiCa ?? [])
  /** "Nghỉ đột xuất": chỉ chủ / quản lý, chỉ hôm nay, và còn ít nhất một người có ca chưa nghỉ. */
  const choNghiDotXuat =
    data !== null &&
    nutNghiDotXuatHien({
      coXepCa: toi.quyen.includes('xep-ca'),
      laHomNay: date === ngayVn(),
      staff: data.staff,
      nghi: data.nghi ?? [],
      // v0.22.3 B9: hôm nay tiệm đã đóng cửa thì không còn gì để "nghỉ đột xuất".
      dongCua: data.dongCua,
    })
  /**
   * Tờ chi tiết đang mở luôn hiện bản MỚI NHẤT của lịch đó trong bảng — tìm theo `itemId` (lịch nhiều dịch vụ có
   * nhiều dòng cùng `appointmentId`, tìm theo nó thì luôn ra dòng ĐẦU). Không còn trong bảng của ngày này nữa thì
   * `daMat`: tờ nói thật và khoá nút thay vì hiện trạng thái lỗi thời (N1, V3 vòng sửa 3.1).
   */
  const { lich: lichMo, daMat: lichDaMat } = layLichMo(chon, data, date)

  return (
    <>
      <header className="dau-man dau-man-lich">
        {/*
         * Điện thoại (vòng 44): CHỈ khối này dính khi cuộn (tên tiệm + tiêu đề + dải báo mất mạng ≈ 73px) — ô ngày + ô
         * chi nhánh cuộn theo nội dung. Trước đây cả đầu trang dính 199px: cùng thanh "Đặt lịch" + thanh dưới còn 357px
         * (320×700) cho lịch. Tablet / máy tính: `display: contents`, vẫn là một hàng như cũ.
         */}
        <div className="dau-dinh">
          <div className="ten-man">
            <DongTenTiem tenTiem={toi.tenTiem} />
            <h1>{laTho ? T.navMyCalendar : T.pageCalendar}</h1>
          </div>
          {/*
           * Dải báo nằm TRONG phần dính (không trong thân trang): lễ tân đang cuộn tới giờ chiều vẫn thấy
           * đang mất mạng, và số đang xem là của lúc nào — không đọc số cũ như số mới (đợt 3 · 3.1).
           */}
          {loiTai !== null && (
            <div className="dai-loi-tai">
              <ThongBao
                loai={loiTai.matMang ? 'canh-bao' : 'loi'}
                lon
                hanhDong={
                  <button type="button" className="nut nut-vien nut-lon" onClick={taiLaiTay}>
                    {T.reload}
                  </button>
                }
              >
                {loiTai.matMang ? T.matMangDangThu : loiTai.thongBao}
                {data !== null && capNhatLuc !== null && (
                  <span className="cap-nhat-trong-bao">{T.capNhatLuc(capNhatLuc)}</span>
                )}
              </ThongBao>
            </div>
          )}
        </div>
        {/* Tiệm chưa có chi nhánh thì chưa có lịch nào để xem — chưa hiện bộ chọn ngày. */}
        {(branches === null || branches.length > 0) && (
          <DieuHuongNgay date={date} onDoi={setDate} />
        )}
        {choDatLich && branchId !== '' && (
          <button
            type="button"
            className="nut nut-chinh an-dien-thoai"
            onClick={() => {
              setDienSan(null)
              setMoDat(true)
            }}
          >
            <Plus size={20} />
            {T.newBooking}
          </button>
        )}
      </header>

      <main className="than-man">
        {choCauHinh && (
          <ChuanBiTiem
            maTiem={toi.maTiem}
            onMo={onMoCauHinh}
            onDatThu={
              choDatLich && branchId !== ''
                ? () => {
                    setDienSan(null)
                    setMoDat(true)
                  }
                : null
            }
            live={toi.miniAppLive === true}
            taiLai={moDat}
            chao={chaoMoTiem}
            {...(onDongChao === undefined ? {} : { onDongChao })}
            onHien={setTheChuanBiHien}
          />
        )}
        {loi !== null && (
          <ThongBao loai="loi" onDong={() => setLoi(null)}>
            {loi}
          </ThongBao>
        )}
        {/* Đợt 3 · 3.4: có người nghỉ mà còn lịch cần chuyển → dải đỏ, đọc sống từ `/day.nghi` (hết lịch là tự biến mất). */}
        {data !== null && choDatLich && daiLichVuong !== null && (
          <ThongBao
            loai="loi"
            lon
            hanhDong={
              <button
                type="button"
                className="nut nut-vien nut-lon"
                onClick={() => setMoNghi('ds')}
              >
                {T.offBandAction}
              </button>
            }
          >
            {daiLichVuong}
          </ThongBao>
        )}
        {/* K9: lịch ngày trước khách đã tới mà chưa thu tiền — chỉ nhắc (không tự đổi gì); "Xem" nhảy tới lịch cũ nhất. */}
        {data !== null && lichChuaDong.length > 0 && (
          <ThongBao
            loai="canh-bao"
            lon
            hanhDong={
              <button type="button" className="nut nut-vien nut-lon" onClick={xemLichChuaDong}>
                {T.xemLichChuaDong}
              </button>
            }
          >
            {T.chuaDongTruoc(lichChuaDong.length)}
          </ThongBao>
        )}
        {/* K2 (ADR-111): chi nhánh đóng cửa cả ngày mà còn lịch (khung rỗng "Tiệm nghỉ ngày này" đã nói rồi khi không còn lịch nào);
            dải đỏ ở trên đã nói "Tiệm nghỉ" thì không lặp lại. Dải này mang LÝ DO chủ tiệm ghi (thợ không thấy lý do). */}
        {data?.dongCua != null &&
          !(choDatLich && daiLichVuong !== null) &&
          data.appointments.some((a) => a.status !== 'cancelled') && (
            <ThongBao loai="canh-bao" lon>
              {T.closedBandReason(data.dongCua.lyDo)}
            </ThongBao>
          )}

        {branches !== null && branches.length === 0 ? (
          // Chủ tiệm đã thấy thẻ "Chuẩn bị tiệm" ở trên — không lặp lại cùng một câu lần nữa.
          choCauHinh ? null : (
            <div className="the">
              <Rong
                icon={<CalendarDays size={28} />}
                tieuDe={T.notReadyTitle}
                moTa={T.notReadyHint}
              />
            </div>
          )
        ) : data === null ? (
          <KhungDangTai />
        ) : (
          <NoiDungNgay
            // Thợ không có ca mà vẫn có lịch: thêm cột của chính thợ để lịch không bị giấu (xem `themCotChoTho`).
            data={themCotChoTho(data, laTho, toi.toi.ten)}
            laTho={laTho}
            date={date}
            capNhatLuc={loiTai === null ? capNhatLuc : null}
            choCauHinh={choCauHinh}
            live={toi.miniAppLive === true}
            anKhungTrong={choCauHinh && theChuanBiHien}
            onMoCauHinh={onMoCauHinh}
            onChon={setChon}
            onDat={choDatLich ? () => setMoDat(true) : null}
            api={api}
            onDaDoi={() => void nap()}
            onDaToi={setVuaToi}
            onLoi={setLoi}
            onHoanTat={toi.quyen.includes('thu-tien.lich') ? setHoanTatCho : undefined}
            tinhTienDuoc={tinhTienDuoc}
            onKhachVao={
              khachVao && branchId !== '' && date === ngayVn() ? () => setMoKhachVao(true) : null
            }
            onNghiDotXuat={choNghiDotXuat ? () => setMoNghi('chon') : null}
            onTinhTien={toi.quyen.includes('thu-tien.lich') ? () => setMoTinhTien(true) : null}
            onTao={
              choDatLich
                ? (staffId, tenKtv, startMin) => {
                    setDienSan({ staffId, tenKtv, startMin })
                    setMoDat(true)
                  }
                : null
            }
            onDoi={choDatLich ? (a, staffId, startMin) => doiLich(a, staffId, startMin) : null}
          />
        )}
        {/* KH-GĐ2 4.2.1: lịch lên đầu; nhắc lịch ngày mai (việc buổi tối) xuống dưới lịch. */}
        {choDatLich && branchId !== '' && <TheCanNhac api={api} branchId={branchId} />}
      </main>

      {choDatLich && branchId !== '' && (
        // Thanh liền trên thanh điều hướng: dải nền trang có lề 16px + nút bo góc (vòng 44) — khe nền rõ ràng giữa nút
        // và thanh dưới thay cho bóng đổ; nội dung cuộn không lọt qua khe.
        <div className="dock-dat-lich chi-dien-thoai">
          <button
            type="button"
            className="nut nut-chinh"
            aria-label={T.newBooking}
            onClick={() => {
              setDienSan(null)
              setMoDat(true)
            }}
          >
            <Plus size={22} />
            {T.newBooking}
          </button>
        </div>
      )}

      {lichMo !== null && (
        <ChiTietLich
          lich={lichMo}
          daMat={lichDaMat}
          choQuay={choDatLich}
          ngay={date}
          tenKtv={data?.staff.find((s) => s.staffId === lichMo.staffId)?.fullName ?? T.unassigned}
          api={api}
          onDong={() => setChon(null)}
          onDaToi={setVuaToi}
          onDaDoi={(thongBao) => {
            setChon(null)
            if (thongBao !== undefined) setBao(thongBao)
            void nap()
          }}
          onDoiGio={
            choDatLich && lichMo.doiDuoc
              ? () => {
                  setDoiGioCho(lichMo)
                  setChon(null)
                }
              : null
          }
          onHoanTat={
            tinhTienDuoc(lichMo)
              ? () => {
                  setHoanTatCho(lichMo)
                  setChon(null)
                }
              : undefined
          }
          onGoiVi={
            thuTien && lichMo.customerId != null
              ? () => {
                  setGoiViCho(lichMo.customerId ?? null)
                  setChon(null)
                }
              : undefined
          }
          coc={{
            choNhan: thuTien,
            choHoan: toi.quyen.includes('hoan-dao-tien'),
            soTienMacDinh: data?.cocMacDinhVnd ?? null,
          }}
          hoSo={{ coAnh: toi.quyen.includes('xem.anh-khach') }}
        />
      )}

      {moTinhTien && (
        <ToTinhTien
          ds={(data?.appointments ?? []).filter(tinhTienDuoc)}
          onChon={(a) => {
            setMoTinhTien(false)
            setHoanTatCho(a)
          }}
          onKhachVao={
            khachVao && branchId !== '' && date === ngayVn()
              ? () => {
                  setMoTinhTien(false)
                  setMoKhachVao(true)
                }
              : null
          }
          onDong={() => setMoTinhTien(false)}
        />
      )}

      {hoanTatCho !== null && (
        <ToHoanTat
          api={api}
          appointmentId={hoanTatCho.appointmentId}
          tenKhachHang={tenKhach(hoanTatCho.customerName)}
          onDong={() => {
            setHoanTatCho(null)
            void nap()
          }}
          onLoi={setLoi}
          onXong={(thongBao) => {
            setHoanTatCho(null)
            setBao({ chu: thongBao })
            void nap()
          }}
          {...(onKhaiTaiKhoan === undefined ? {} : { onKhaiTaiKhoan })}
        />
      )}

      {goiViCho !== null && branchId !== '' && (
        <ToGoiViKhach
          key={goiViCho}
          api={api}
          toi={toi}
          customerId={goiViCho}
          branchId={branchId}
          onDong={() => setGoiViCho(null)}
          onMoKhac={setGoiViCho}
          onDaGop={() => void nap()}
        />
      )}

      {moKhachVao && branchId !== '' && (
        <ToKhachVao
          api={api}
          branchId={branchId}
          services={services}
          choNhapSdt={toi.quyen.includes('tim-khach')}
          onDong={() => {
            setMoKhachVao(false)
            void nap()
          }}
          onXong={(ten) => {
            setMoKhachVao(false)
            setBao({ chu: T.walkInDone(ten) })
            void nap()
          }}
        />
      )}

      {moDat && branchId !== '' && (
        <BookSheet
          api={api}
          branchId={branchId}
          tenChiNhanh={tenChiNhanh}
          date={date}
          services={services}
          {...(dienSan === null ? {} : { dienSan })}
          {...(sdtDau === undefined ? {} : { sdtDau })}
          // Đóng tờ sau lỗi hết giờ ("chưa chắc đã lưu") thì tải lại để thấy lịch có thật hay không.
          onClose={() => {
            setMoDat(false)
            void nap()
          }}
          onBooked={(code, ngayDat) => {
            setMoDat(false)
            setBao({ chu: T.bookedToast(code) })
            if (ngayDat !== date) setDate(ngayDat)
            else void nap()
          }}
        />
      )}

      {doiGioCho !== null && data !== null && branchId !== '' && (
        <ToDoiGio
          lich={doiGioCho}
          api={api}
          branchId={branchId}
          date={date}
          nhanVien={nhanVienDoiGio(data.staff, data.nghi ?? [])}
          onDong={() => setDoiGioCho(null)}
          onXong={async (staffId, startMin, ngay) => {
            const a = doiGioCho
            setDoiGioCho(null)
            await doiLich(a, staffId, startMin, ngay)
          }}
        />
      )}

      {moNghi !== null && data !== null && branchId !== '' && (
        <ToNghiDotXuat
          api={api}
          branchId={branchId}
          date={date}
          data={data}
          buocDau={moNghi}
          choNghi={toi.quyen.includes('xep-ca')}
          choDongCua={toi.quyen.includes('cau-hinh.tiem')}
          onDong={() => setMoNghi(null)}
          onDaDoi={() => void nap()}
          onMoLich={(a) => {
            setMoNghi(null)
            setChon(a)
          }}
          onDoiGio={(a) => {
            setMoNghi(null)
            setDoiGioCho(a)
          }}
        />
      )}

      {vuaDoi !== null && (
        <ThongBao
          loai={kieuToast(vuaDoi.baoKhach)}
          noi
          onDong={() => setVuaDoi(null)}
          hanhDong={
            <button
              type="button"
              className="nut nut-nhat nut-hoan-tac"
              onClick={() => void hoanTacDoi()}
            >
              {T.undo}
            </button>
          }
        >
          {toastSauDoi(tenKhach(vuaDoi.a.customerName), vuaDoi.baoKhach)}
        </ThongBao>
      )}
      {vuaToi !== null && vuaDoi === null && (
        <ThongBao
          loai="thanh-cong"
          noi
          onDong={() => setVuaToi(null)}
          hanhDong={
            <button
              type="button"
              className="nut nut-nhat nut-hoan-tac"
              onClick={() => void hoanTac(vuaToi)}
            >
              {T.undo}
            </button>
          }
        >
          {T.checkedInToast(tenKhach(vuaToi.customerName))}
        </ThongBao>
      )}
      {bao !== null && vuaToi === null && (
        <ThongBao loai={kieuToast(bao.baoKhach)} noi onDong={() => setBao(null)}>
          {bao.chu}
        </ThongBao>
      )}
    </>
  )
}

/**
 * ‹ [Thứ Hai, 21/09/2026 · Hôm nay] › — một khối liền. Nhãn ngày mở bộ chọn ngày của máy.
 * Ngày khác hôm nay: nút "Hôm nay" là nút thật (nền nhạt, không phải chữ xám); ở điện thoại hẹp (≤ 379px) nó xuống hàng
 * riêng trong khối để nhãn ngày giữ trọn chỗ — "Thứ Năm, 15/10" không bao giờ bị cắt ở 320px (vòng 48).
 */
export function DieuHuongNgay({ date, onDoi }: { date: string; onDoi: (d: string) => void }) {
  const homNay = ngayVn()
  return (
    <div className={`dieu-huong-ngay${date === homNay ? '' : ' co-ve-hom-nay'}`}>
      <button
        type="button"
        className="nut nut-icon"
        aria-label={T.prevDay}
        onClick={() => onDoi(themNgay(date, -1))}
      >
        <ChevronLeft size={22} />
      </button>
      <label className="nhan-ngay">
        <CalendarDays size={18} />
        <span className="dai">{nhanNgayDayDu(date)}</span>
        <span className="ngan">{nhanNgayNgan(date)}</span>
        {date === homNay && <span className="dau-cham">{T.jumpToday}</span>}
        <input
          type="date"
          aria-label={T.pickDate}
          value={date}
          onClick={(e) => moBangChonNgay(e.currentTarget)}
          onChange={(e) => {
            if (e.target.value !== '') onDoi(e.target.value)
          }}
        />
      </label>
      <button
        type="button"
        className="nut nut-icon"
        aria-label={T.nextDay}
        onClick={() => onDoi(themNgay(date, 1))}
      >
        <ChevronRight size={22} />
      </button>
      {date !== homNay && (
        <button type="button" className="nut nut-nhat nut-ve-hom-nay" onClick={() => onDoi(homNay)}>
          {T.jumpToday}
        </button>
      )}
    </div>
  )
}

function KhungDangTai() {
  return (
    <>
      <Xuong cao={24} rong="40%" />
      <div className="the the-dem" style={{ display: 'grid', gap: 12 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Xuong key={i} cao={48} />
        ))}
      </div>
    </>
  )
}

function NoiDungNgay({
  data,
  laTho,
  date,
  capNhatLuc,
  choCauHinh,
  live,
  anKhungTrong,
  onMoCauHinh,
  onChon,
  onDat,
  api,
  onDaDoi,
  onDaToi,
  onLoi,
  onHoanTat,
  tinhTienDuoc,
  onKhachVao,
  onNghiDotXuat,
  onTinhTien,
  onTao,
  onDoi,
}: {
  data: DayBoardData
  /** Vai kỹ thuật viên: máy chủ chỉ trả ca của chính thợ → màn rỗng nói "bạn không có ca", không bảo xếp ca. */
  laTho: boolean
  date: string
  /** "hh:mm" lần tải thành công gần nhất; null = không hiện (đang lỗi thì dải báo ở đầu trang đã nói giờ này). */
  capNhatLuc: string | null
  choCauHinh: boolean
  /** Ứng dụng đặt lịch trên Zalo đã chạy thật (`toi.miniAppLive`): chưa thì không hứa "khách đặt qua Zalo". */
  live: boolean
  /** Thẻ "Chuẩn bị tiệm" đang hiện ở trên: ẩn hộp rỗng này (khỏi ba nút "Đặt lịch" cùng lúc). */
  anKhungTrong: boolean
  onMoCauHinh: (the: TheCauHinh) => void
  onChon: (a: DayAppointment) => void
  onDat: (() => void) | null
  api: Api
  onDaDoi: () => void
  onDaToi: (a: DayAppointment) => void
  onLoi: (s: string) => void
  onHoanTat?: ((a: DayAppointment) => void) | undefined
  /** ADR-078: lịch này người đang xem tính tiền được không (thợ tự thu: không tính lịch chung). */
  tinhTienDuoc: (a: DayAppointment) => boolean
  /** Hôm nay + vai đặt lịch: nút "Khách vào" ở dòng tóm tắt. */
  onKhachVao: (() => void) | null
  /** Đợt 3 · 3.4: chủ / quản lý, hôm nay — nút "Nghỉ đột xuất" ở dòng tóm tắt. */
  onNghiDotXuat: (() => void) | null
  /** Vai thu tiền: nút "Tính tiền" ngay đầu lịch, không nằm trong menu (4.2.4). */
  onTinhTien: (() => void) | null
  onTao: ((staffId: string, tenKtv: string, startMin: number) => void) | null
  onDoi: ((a: DayAppointment, staffId: string, startMin: number) => Promise<void>) | null
}) {
  const hienThi = data.appointments.filter((a) => a.status !== 'cancelled')
  const dem = (...tt: string[]) => hienThi.filter((a) => tt.includes(a.status)).length
  const dsRef = useRef<HTMLUListElement>(null)
  /*
   * Điện thoại, hôm nay: cuộn tới lịch đầu tiên chưa kết thúc — 21 giờ mở màn mà thấy lịch 09:00
   * là phải lướt qua 6 thẻ đã qua (vòng 2). Tablet có vạch "bây giờ" nên không cần.
   * Chỉ cuộn khi thẻ đó NẰM NGOÀI màn hình (`nearest`): `start` kéo thẻ lên sát mép trên, nên chỉ có vài
   * thẻ thôi cũng đẩy ô chọn ngày chui dưới đầu trang dính (14/10, iPhone) — vào màn là mất ngày.
   */
  useEffect(() => {
    const ul = dsRef.current
    if (ul === null || date !== ngayVn() || ul.offsetParent === null) return
    const bayGio = phutBayGio()
    const dau = [...ul.querySelectorAll<HTMLElement>('[data-ket-thuc]')].find(
      (el) => Number(el.dataset.ketThuc) > bayGio,
    )
    dau?.scrollIntoView({ block: 'nearest' })
  }, [date])

  // Rỗng vì sao: tính trên dữ liệu gốc + vai, không trên mảng đã lọc theo vai (xem `bangRong`).
  const rong = bangRong(data, laTho)
  if (rong === 'dong-cua') {
    return (
      <div className="the the-dong-cua">
        <Rong
          icon={<CalendarOff size={28} />}
          tieuDe={T.closedTitle}
          moTa={
            laTho
              ? T.closedHintTho
              : (data.dongCua ?? null) !== null
                ? T.closedHintDong
                : T.closedHint
          }
          hanhDong={
            choCauHinh ? (
              <button
                type="button"
                className="nut nut-vien"
                onClick={() => onMoCauHinh('chi-nhanh')}
              >
                {T.goConfig}
              </button>
            ) : undefined
          }
        />
      </div>
    )
  }
  if (rong === 'tho-khong-ca') {
    return (
      <div className="the">
        <Rong
          icon={<CalendarOff size={28} />}
          tieuDe={date === ngayVn() ? T.noShiftMineTitle : T.noShiftMineOtherDayTitle}
          moTa={T.noShiftMineHint}
        />
      </div>
    )
  }
  if (rong === 'chua-xep-ca') {
    return (
      <div className="the">
        <Rong
          icon={<Users size={28} />}
          tieuDe={T.noStaffTitle}
          moTa={T.noStaffHint}
          hanhDong={
            choCauHinh ? (
              <button
                type="button"
                className="nut nut-chinh"
                onClick={() => onMoCauHinh('nhan-vien')}
              >
                {T.goConfig}
              </button>
            ) : undefined
          }
        />
      </div>
    )
  }

  return (
    <>
      {/* KH-GĐ2 4.2.2: một dòng chữ thay bốn ô số; "Khách vào" cùng hàng (chỉ hôm nay). */}
      <div className="thanh-ngay">
        {/* Giờ cập nhật nằm ngay dưới tóm tắt (một khối), không còn là một dòng riêng cách 16px (vòng 44). */}
        <div className="khoi-tom-tat">
          <p className="tom-tat-ngay">
            {T.daySummary(hienThi.length, dem('checked_in', 'in_service'), dem('done'))}
          </p>
          {capNhatLuc !== null && <p className="cap-nhat-luc">{T.capNhatLuc(capNhatLuc)}</p>}
        </div>
        <div className="nut-ngay">
          {onKhachVao !== null && (
            <button type="button" className="nut nut-vien" onClick={onKhachVao}>
              <UserRoundPlus size={18} />
              {T.walkInTitle}
            </button>
          )}
          {onTinhTien !== null && <NutTinhTien onMo={onTinhTien} />}
          {onNghiDotXuat !== null && (
            <button
              type="button"
              className="nut nut-vien nut-nghi-dot-xuat"
              aria-label={T.offTodayButton}
              title={T.offTodayButton}
              onClick={onNghiDotXuat}
            >
              <CalendarOff size={18} />
              {/* Điện thoại hẹp: chữ ngắn "Báo nghỉ" cho ba nút vừa MỘT hàng; rộng hơn thì tên đầy đủ. */}
              <span className="chu-nghi-day">{T.offTodayButton}</span>
              <span className="chu-nghi-gon">{T.offTodayShort}</span>
            </button>
          )}
        </div>
      </div>

      {/* 4.2.3: khung lớn chỉ khi tiệm CHƯA TỪNG có lịch; ngày trống thường thì chỉ lưới trống. */}
      {hienThi.length === 0 && data.tiemCoLich === false && !anKhungTrong && (
        <div className="the">
          <Rong
            icon={<CalendarDays size={28} />}
            tieuDe={T.emptyDayTitle}
            moTa={live ? T.emptyDayHint : T.emptyDayHintChuaLive}
            hanhDong={
              onDat !== null ? (
                <button type="button" className="nut nut-chinh" onClick={onDat}>
                  <Plus size={20} />
                  {T.newBooking}
                </button>
              ) : undefined
            }
          />
        </div>
      )}
      <div className="an-dien-thoai">
        <LuoiLich data={data} date={date} onChon={onChon} onTao={onTao} onDoi={onDoi} />
      </div>

      <div className="chi-dien-thoai">
        {hienThi.length === 0 ? (
          data.tiemCoLich === false ? null : (
            <p className="chu-phu">{T.dayEmptyShort}</p>
          )
        ) : (
          <ul className="ds-lich" ref={dsRef}>
            {hienThi.map((a) => (
              <DongLich
                key={a.itemId}
                a={a}
                ngay={date}
                api={api}
                onChon={() => onChon(a)}
                onDaDoi={onDaDoi}
                onDaToi={onDaToi}
                onLoi={onLoi}
                onHoanTat={tinhTienDuoc(a) ? onHoanTat : undefined}
              />
            ))}
          </ul>
        )}
      </div>
    </>
  )
}

/** Bước lưới khi tạo / kéo lịch trên bảng: 15 phút, khớp bước khe giờ mặc định của tiệm. */
const BUOC_PHUT = 15
/** Kéo quá ngần này điểm ảnh mới tính là kéo; ít hơn là chạm (mở tờ chi tiết). */
const NGUONG_KEO = 8

interface DangKeo {
  a: DayAppointment
  x0: number
  y0: number
  dangKeo: boolean
  staffId: string
  startMin: number
}

function LuoiLich({
  data,
  date,
  onChon,
  onTao,
  onDoi,
}: {
  data: DayBoardData
  date: string
  onChon: (a: DayAppointment) => void
  /** Chạm ô trống của một cột → tạo lịch cho đúng KTV, đúng giờ. `null`: vai này không đặt lịch. */
  onTao: ((staffId: string, tenKtv: string, startMin: number) => void) | null
  /** Thả khối lịch ở chỗ mới. Trả Promise để giữ bóng mờ cho tới khi máy chủ trả lời. */
  onDoi: ((a: DayAppointment, staffId: string, startMin: number) => Promise<void>) | null
}) {
  const cuonRef = useRef<HTMLDivElement>(null)
  // K2: hợp giờ mở cửa với giờ các lịch trong ngày (ngày đóng cửa mà còn lịch → `openWindows` rỗng nhưng vẫn phải vẽ lưới).
  const { tuPhut, denPhut } = khungLuoi(data.openWindows, data.appointments)
  const cao = (denPhut - tuPhut) * PX_PHUT
  const hienThi = data.appointments.filter((a) => a.status !== 'cancelled')
  const keoRef = useRef<DangKeo | null>(null)
  /** Bóng mờ của khối đang kéo (hoặc đang chờ máy chủ xác nhận chỗ mới). */
  const [bong, setBong] = useState<{ itemId: string; staffId: string; startMin: number } | null>(
    null,
  )
  const [dangLuu, setDangLuu] = useState(false)
  /** Chuột rê trên ô trống: gợi ý "+ 10:15" — chỉ ở máy có chuột; tablet thì chạm là tạo. */
  const [ro, setRo] = useState<{ staffId: string; startMin: number } | null>(null)

  const cot = useMemo(() => {
    const c = data.staff.map((s) => ({
      id: s.staffId as string | null,
      ten: s.fullName,
      ca: s.shifts,
    }))
    if (hienThi.some((a) => a.staffId === null)) c.push({ id: null, ten: T.unassigned, ca: [] })
    return c
  }, [data.staff, hienThi])

  const nguoiNghi = useMemo(() => new Set((data.nghi ?? []).map((n) => n.staffId)), [data.nghi])
  const bayGio = date === ngayVn() ? phutBayGio() : null
  const coVach = bayGio !== null && bayGio >= tuPhut && bayGio <= denPhut

  // Sau giờ lịch cuối cùng (tiệm đã đóng): cuộn về lịch ĐẦU TIÊN — cuộn tới "bây giờ" thì chỉ thấy
  // lưới trống cuối ngày (vòng 5).
  const cuoiNgay = Math.max(0, ...hienThi.map((a) => a.endMin))
  const dauNgay = Math.min(...hienThi.map((a) => a.startMin))
  const mocCuon =
    bayGio === null ? null : bayGio > cuoiNgay && hienThi.length > 0 ? dauNgay : bayGio

  // Mở màn là thấy ngay giờ hiện tại, không phải 08:00 sáng.
  useEffect(() => {
    const el = cuonRef.current
    if (el === null || mocCuon === null) return
    el.scrollTop = Math.max(0, (mocCuon - tuPhut) * PX_PHUT - 120)
  }, [mocCuon, tuPhut])

  const moc: number[] = []
  for (let m = tuPhut; m <= denPhut; m += 60) moc.push(m)

  /** Phút của điểm chạm trong một cột, làm tròn xuống lưới 15 phút. */
  function phutTai(el: HTMLElement, clientY: number): number {
    const y = clientY - el.getBoundingClientRect().top
    const p = tuPhut + Math.floor(y / PX_PHUT / BUOC_PHUT) * BUOC_PHUT
    return Math.min(Math.max(p, tuPhut), denPhut - BUOC_PHUT)
  }

  /** Cột KTV nằm dưới ngón tay / con trỏ lúc kéo. */
  function cotDuoi(x: number, y: number): string | null {
    for (const el of document.elementsFromPoint(x, y)) {
      const id = (el as HTMLElement).dataset?.cotKtv
      if (id !== undefined && id !== '') return id
    }
    return null
  }

  /** Ô trống (nền cột hoặc vùng ca), không phải một khối lịch. */
  function laOTrong(e: React.SyntheticEvent<HTMLElement>): boolean {
    const t = e.target as HTMLElement
    return t === e.currentTarget || t.classList.contains('lich-ca')
  }

  function batDauKeo(e: React.PointerEvent<HTMLButtonElement>, a: DayAppointment) {
    if (onDoi === null || !a.doiDuoc || a.staffId === null || dangLuu) return
    keoRef.current = {
      a,
      x0: e.clientX,
      y0: e.clientY,
      dangKeo: false,
      staffId: a.staffId,
      startMin: a.startMin,
    }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  function dangKeoDi(e: React.PointerEvent<HTMLButtonElement>) {
    const k = keoRef.current
    if (k === null) return
    const dx = e.clientX - k.x0
    const dy = e.clientY - k.y0
    if (!k.dangKeo && Math.hypot(dx, dy) < NGUONG_KEO) return
    k.dangKeo = true
    const dai = k.a.endMin - k.a.startMin
    const lech = Math.round(dy / PX_PHUT / BUOC_PHUT) * BUOC_PHUT
    k.startMin = Math.min(Math.max(k.a.startMin + lech, tuPhut), denPhut - dai)
    k.staffId = cotDuoi(e.clientX, e.clientY) ?? k.staffId
    setBong({ itemId: k.a.itemId, staffId: k.staffId, startMin: k.startMin })
  }

  function thaRa() {
    const k = keoRef.current
    keoRef.current = null
    if (k === null) return
    if (!k.dangKeo) {
      // Chạm, không kéo: mở tờ chi tiết như cũ.
      onChon(k.a)
      return
    }
    const doiCho = k.startMin !== k.a.startMin || k.staffId !== k.a.staffId
    if (!doiCho || onDoi === null) {
      setBong(null)
      return
    }
    setDangLuu(true)
    void onDoi(k.a, k.staffId, k.startMin).finally(() => {
      setDangLuu(false)
      setBong(null)
    })
  }

  return (
    <div className="the lich-khung">
      <div className="lich-cuon" ref={cuonRef}>
        <div className="lich-luoi" style={{ '--so-cot': cot.length } as React.CSSProperties}>
          <div className="lich-goc" />
          {cot.map((c) => {
            const so = hienThi.filter((a) => a.staffId === c.id).length
            const nghi = c.id !== null && nguoiNghi.has(c.id)
            return (
              <div key={c.id ?? 'chua-xep'} className={`lich-dau-cot ${nghi ? 'dang-nghi' : ''}`}>
                <Avatar ten={c.ten} co="nho" />
                <div style={{ minWidth: 0 }}>
                  <b>{c.ten}</b>
                  <span>
                    {nghi ? (date === ngayVn() ? T.offColumnToday : T.offColumn) : T.staffCount(so)}
                  </span>
                </div>
              </div>
            )
          })}

          <div className="lich-truc" style={{ height: cao }}>
            {moc.map((m) => (
              <span
                key={m}
                // Nhãn giờ ĐẦU nằm dưới vạch (không đè lên tiêu đề cột dính) — trục giờ thấy ngay bắt đầu từ mấy giờ.
                className={m === tuPhut ? 'moc moc-dau' : 'moc'}
                style={{ top: (m - tuPhut) * PX_PHUT }}
              >
                {gioPhut(m)}
              </span>
            ))}
          </div>
          {cot.map((c) => {
            const ktvId = c.id
            // Cột của người đang nghỉ / không còn ca ngày này: không tạo lịch bằng chạm ô trống (máy chủ cũng không còn khe nào của họ).
            const taoDuoc =
              onTao !== null &&
              ktvId !== null &&
              !nguoiNghi.has(ktvId) &&
              c.ca.length > 0 &&
              // K2: ngày tiệm đóng cửa — máy chủ cũng từ chối `CLOSED`, đừng để chủ tiệm chạm rồi mới gặp lỗi.
              (data.dongCua ?? null) === null
            return (
              // biome-ignore lint/a11y/useKeyWithClickEvents: ô trống là lối tắt bằng chạm; bàn phím dùng nút "Đặt lịch cho khách" ở đầu trang.
              // biome-ignore lint/a11y/noStaticElementInteractions: như trên — không phải lối DUY NHẤT để tạo lịch.
              <div
                key={ktvId ?? 'chua-xep'}
                className={`lich-cot ${taoDuoc ? 'tao-duoc' : ''}`}
                data-cot-ktv={ktvId ?? ''}
                style={{ height: cao }}
                onClick={(e) => {
                  if (!taoDuoc || ktvId === null || onTao === null || !laOTrong(e)) return
                  onTao(ktvId, c.ten, phutTai(e.currentTarget, e.clientY))
                }}
                onPointerMove={(e) => {
                  if (!taoDuoc || ktvId === null || e.pointerType !== 'mouse') return
                  if (!laOTrong(e)) {
                    setRo(null)
                    return
                  }
                  setRo({ staffId: ktvId, startMin: phutTai(e.currentTarget, e.clientY) })
                }}
                onPointerLeave={() => setRo(null)}
              >
                {c.ca.map((ca) => (
                  <div
                    key={`${ca.startMin}-${ca.endMin}`}
                    className="lich-ca"
                    style={{
                      top: (ca.startMin - tuPhut) * PX_PHUT,
                      height: (ca.endMin - ca.startMin) * PX_PHUT,
                      backgroundPositionY: -((ca.startMin - tuPhut) * PX_PHUT),
                    }}
                  />
                ))}
                {ro !== null && ro.staffId === ktvId && bong === null && (
                  <div
                    className="goi-y-tao"
                    style={{
                      top: (ro.startMin - tuPhut) * PX_PHUT,
                      height: BUOC_PHUT * 2 * PX_PHUT,
                    }}
                  >
                    + {gioPhut(ro.startMin)}
                  </div>
                )}
                {hienThi
                  .filter((a) => a.staffId === ktvId)
                  .map((a) => {
                    const h = Math.max(36, (a.endMin - a.startMin) * PX_PHUT - 3)
                    const keoDuoc = onDoi !== null && a.doiDuoc
                    const lop = [
                      'khoi-lich',
                      `tt-${a.status}`,
                      laTre(a, bayGio) ? 'tre' : '',
                      keoDuoc ? 'keo-duoc' : '',
                      bong?.itemId === a.itemId ? 'dang-keo' : '',
                    ].join(' ')
                    return (
                      <button
                        key={a.itemId}
                        type="button"
                        className={lop}
                        style={{ top: (a.startMin - tuPhut) * PX_PHUT + 1, height: h }}
                        onPointerDown={(e) => batDauKeo(e, a)}
                        onPointerMove={dangKeoDi}
                        onPointerUp={thaRa}
                        onPointerCancel={() => {
                          keoRef.current = null
                          setBong(null)
                        }}
                        onClick={(e) => {
                          // Khối kéo được: chạm/kéo đã xử lý ở pointerup. Chỉ bàn phím mới tới đây
                          // (Enter/Space tạo click không có pointer) → mở chi tiết.
                          if (!keoDuoc || e.detail === 0) onChon(a)
                        }}
                      >
                        {/*
                          Khối nói đủ "ai · mấy giờ · làm gì" theo độ cao (chủ dự án 22/09: khối
                          trống trông không chuyên nghiệp). Khối ngắn vẫn có giờ ngay dòng đầu.
                        */}
                        <span className="noi-khoi">
                          <span className={`dong-1 ${h >= CAO_DONG_3 ? 'be-ten' : ''}`}>
                            {h < CAO_DONG_2 && <span className="gio">{gioPhut(a.startMin)}</span>}
                            <span className="ten">{tenKhach(a.customerName)}</span>
                            {chuNhanTre(a, bayGio) !== null && (
                              <span className="nhan-tre">{chuNhanTre(a, bayGio)}</span>
                            )}
                            {(a.loiNhan?.length ?? 0) > 0 && <DauLoiNhan />}
                            {a.source === 'miniapp' && (
                              <span className="nhan-zalo">{T.sourceMiniapp}</span>
                            )}
                            {a.status === 'done' && a.daThu === false && (
                              <span className="nhan-chua-thu">{G.chuaThu}</span>
                            )}
                          </span>
                          {h >= CAO_DONG_2 && (
                            <span className={`dong-2 ${h >= CAO_DONG_2_BE ? 'be-dong' : ''}`}>
                              <span className="gio">
                                {gioPhut(a.startMin)}–{gioPhut(a.endMin)}
                              </span>
                              {' · '}
                              {tenDichVu(a.serviceName)}
                              <DauTrangThaiLich
                                ngan
                                soLanDoi={a.soLanDoiGio}
                                gioCu={a.doiGioTu}
                                daXacNhan={a.daXacNhan}
                                chuaXacNhan={a.chuaXacNhan}
                              />
                            </span>
                          )}
                          {h >= CAO_DONG_3 && (
                            <span className="dong-3">
                              <i className="cham-tt" />
                              {TRANG_THAI[a.status] ?? a.status}
                              {a.customerPhone !== null && ` · ${hienSdt(a.customerPhone)}`}
                            </span>
                          )}
                          {h >= CAO_DONG_4 && a.priceVnd !== null && (
                            <span className="dong-4">{tienVnd(a.priceVnd)}</span>
                          )}
                        </span>
                      </button>
                    )
                  })}
                {bong !== null && bong.staffId === ktvId && (
                  <BongKeo
                    a={hienThi.find((x) => x.itemId === bong.itemId)}
                    startMin={bong.startMin}
                    tuPhut={tuPhut}
                    dangLuu={dangLuu}
                  />
                )}
              </div>
            )
          })}
          {coVach && bayGio !== null && (
            <div className="vach-bay-gio" style={{ top: 64 + (bayGio - tuPhut) * PX_PHUT }} />
          )}
        </div>
      </div>
      <div className="chu-giai">
        {(['booked', 'checked_in', 'in_service', 'done', 'no_show'] as const).map((tt) => (
          <span key={tt} className={`tt-${tt}`}>
            <i />
            {TRANG_THAI[tt]}
          </span>
        ))}
        {/* Chờ duyệt / chờ cọc: chỉ hiện chú giải khi hôm nay có lịch như vậy (ui-critic v21). */}
        {hienThi.some((a) => a.status === 'pending' || a.status === 'awaiting_deposit') && (
          <span className="tt-awaiting_deposit">
            <i />
            {T.legendWaiting}
          </span>
        )}
        {/* 4.2.5: chỉ hiện khi đang có lịch trễ — không thêm chữ thừa cho ngày bình thường. */}
        {hienThi.some((a) => laTre(a, bayGio)) && (
          <span className="chu-giai-tre">
            <i />
            {T.lateTag}
          </span>
        )}
        {onDoi !== null && <span className="meo-keo">{T.dragHint}</span>}
      </div>
    </div>
  )
}

/** Bóng mờ ở chỗ sẽ thả: giờ mới hiện rõ để lễ tân đọc được TRƯỚC khi nhấc tay. */
function BongKeo({
  a,
  startMin,
  tuPhut,
  dangLuu,
}: {
  a: DayAppointment | undefined
  startMin: number
  tuPhut: number
  dangLuu: boolean
}) {
  if (a === undefined) return null
  const dai = a.endMin - a.startMin
  const h = Math.max(36, dai * PX_PHUT - 3)
  return (
    <div
      className={`khoi-lich khoi-bong tt-${a.status} ${dangLuu ? 'dang-luu' : ''}`}
      style={{ top: (startMin - tuPhut) * PX_PHUT + 1, height: h }}
    >
      <span className="noi-khoi">
        <span className="dong-1">
          <span className="gio">
            {gioPhut(startMin)} – {gioPhut(startMin + dai)}
          </span>
        </span>
        <span className="dong-2">{dangLuu ? T.moving : tenKhach(a.customerName)}</span>
      </span>
    </div>
  )
}

function DongLich({
  a,
  ngay,
  api,
  onChon,
  onDaDoi,
  onDaToi,
  onLoi,
  onHoanTat,
}: {
  a: DayAppointment
  ngay: string
  api: Api
  onChon: () => void
  onDaDoi: () => void
  onDaToi: (a: DayAppointment) => void
  onLoi: (s: string) => void
  /** Vai thu tiền: "Xong" mở tờ hoàn tất (trừ gói / ví) thay vì đổi trạng thái thẳng. */
  onHoanTat?: ((a: DayAppointment) => void) | undefined
}) {
  const [dang, setDang] = useState(false)
  // Lịch ngày mai trở đi: chưa có "bước kế tiếp" nào — khách chưa thể tới.
  const chinh =
    ngay <= ngayVn() ? hanhDongCua(a.status, a.source).find((h) => h.kind === 'primary') : undefined

  async function lam(to: string) {
    if (to === 'done' && onHoanTat !== undefined) {
      onHoanTat(a)
      return
    }
    setDang(true)
    try {
      await buocKe(api, a.appointmentId, to)
      if (to === 'checked_in') onDaToi(a)
      onDaDoi()
    } catch (e) {
      onLoi(e instanceof ApiFailure ? e.messageVi : T.statusFailed)
    } finally {
      setDang(false)
    }
  }

  const bayGio = ngay === ngayVn() ? phutBayGio() : null
  const tre = laTre(a, bayGio)
  return (
    <li className={`the dong-lich tt-${a.status}${tre ? ' tre' : ''}`} data-ket-thuc={a.endMin}>
      <div className="cot-gio">
        <b>{gioPhut(a.startMin)}</b>
        <span>{gioPhut(a.endMin)}</span>
      </div>
      <div style={{ minWidth: 0 }}>
        <div className="ten-khach">
          <b>{tenKhach(a.customerName)}</b>
          {(a.loiNhan?.length ?? 0) > 0 && <DauLoiNhan />}
          <span className={`vien-tt tt-${a.status}`}>{TRANG_THAI[a.status] ?? a.status}</span>
          {tre && <span className="nhan-tre">{chuNhanTre(a, bayGio)}</span>}
          {a.status === 'done' && a.daThu === false && (
            <span className="nhan-chua-thu">{G.chuaThu}</span>
          )}
          <DauTrangThaiLich
            soLanDoi={a.soLanDoiGio}
            gioCu={a.doiGioTu}
            daXacNhan={a.daXacNhan}
            chuaXacNhan={a.chuaXacNhan}
          />
        </div>
        <p className="dich-vu">
          <CacCum
            cac={[tenDichVu(a.serviceName), a.priceVnd !== null ? tienVnd(a.priceVnd) : null]}
          />
        </p>
        <div className="hang-hanh-dong">
          {a.status === 'done' && a.daThu === false && onHoanTat !== undefined && (
            <button type="button" className="nut nut-buoc" onClick={() => onHoanTat(a)}>
              {G.collectNow}
            </button>
          )}
          {chinh !== undefined && (
            <button
              type="button"
              className="nut nut-buoc"
              disabled={dang}
              onClick={() => void lam(chinh.to)}
            >
              {chinh.label}
            </button>
          )}
          <button
            type="button"
            className="nut nut-vien nut-icon"
            aria-label={T.moreActions}
            onClick={onChon}
          >
            <MoreHorizontal size={20} />
          </button>
        </div>
      </div>
    </li>
  )
}

/**
 * Bước thợ được bấm, theo cặp từ → đến — khớp `BUOC_CUA_THO` ở máy chủ (quyen.ts). Việc khác (huỷ,
 * không đến, nhận / từ chối, chốt lịch giữ chỗ) là của quầy: thợ không thấy nút, máy chủ cũng chặn.
 */
const BUOC_CUA_THO: Record<string, readonly string[]> = {
  booked: ['checked_in'],
  checked_in: ['in_service', 'booked'],
  in_service: ['done', 'checked_in'],
}

/** Tờ chi tiết: thông tin + **một** nút chính (bước kế tiếp); việc phá huỷ phải xác nhận. */
export function ChiTietLich({
  lich,
  ngay,
  tenKtv,
  api,
  onDong,
  onDaDoi,
  onDaToi,
  onDoiGio: onDoiGioGoc,
  onXemTrenLich,
  onHoanTat: onHoanTatGoc,
  onGoiVi,
  coc: cocGoc,
  hoSo,
  choQuay,
  daMat = false,
}: {
  lich: DayAppointment
  /** Ngày của lịch — lịch ngày mai trở đi chỉ còn "Huỷ lịch", chưa thể "Khách đã tới". */
  ngay: string
  tenKtv: string
  api: Api
  onDong: () => void
  /** `thongBao`: câu cho quầy sau khi HUỶ / TỪ CHỐI (khách được báo bằng cách nào) — màn cha hiện ở toast. */
  onDaDoi: (thongBao?: ToastQuay) => void
  onDaToi: (a: DayAppointment) => void
  /** Mở tờ "Đổi giờ" — `null` khi lịch không dời được hoặc vai này không đặt lịch. */
  onDoiGio: (() => void) | null
  /** Từ danh sách: nhảy sang màn lịch đúng ngày của lịch này. */
  onXemTrenLich?: () => void
  /** Vai thu tiền: "Xong" mở tờ hoàn tất (trừ gói / ví). */
  onHoanTat?: (() => void) | undefined
  /** Vai thu tiền + lịch có khách: mở gói & ví của khách. */
  onGoiVi?: (() => void) | undefined
  /** N2: quyền + số tiền cọc mặc định cho mục "Tiền cọc". Thiếu = không hiện mục. */
  coc?: { choNhan: boolean; choHoan: boolean; soTienMacDinh: string | null } | undefined
  /** N3: "Hồ sơ buổi làm" (lần trước, ghi chú, ảnh). Thiếu = không hiện mục. */
  hoSo?: { coAnh: boolean } | undefined
  /**
   * Vai có quyền `dat-lich` (quầy / quản lý / chủ). Thiếu quyền (thợ) thì KHÔNG hiện Huỷ lịch, Không
   * đến, Nhận / Từ chối — máy chủ từ chối những việc đó với thợ (phản ánh 25/09).
   */
  choQuay: boolean
  /**
   * Lịch không còn trong bảng của ngày đang xem (đã huỷ / dời ngày / máy khác đổi): tờ nói thật và KHOÁ mọi nút ghi,
   * không hiện trạng thái lỗi thời như còn bấm được (V3 vòng sửa 3.1).
   */
  daMat?: boolean
}) {
  // Lịch đã mất khỏi bảng: bỏ mọi đường ghi (đổi giờ, tính tiền, cọc); chỉ còn xem và đóng.
  const onDoiGio = daMat ? null : onDoiGioGoc
  const onHoanTat = daMat ? undefined : onHoanTatGoc
  const coc = daMat ? undefined : cocGoc
  const coGoiVi = useTinhNang('goi-vi')
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  const [hoi, setHoi] = useState<'cancelled' | 'no_show' | 'tu-choi' | null>(null)
  /** Lý do huỷ / từ chối — khách đọc câu này trong Zalo (N1: bắt buộc, máy chủ cũng chặn). */
  const [lyDo, setLyDo] = useState('')
  const canLyDo = hoi === 'cancelled' || hoi === 'tu-choi'
  // V4: hộp xác nhận "Không đến" / "Huỷ" đang mở mà lịch tự tải lại sang trạng thái khác (máy khác vừa đổi) → đóng
  // hộp, không để người bấm xác nhận một việc cho trạng thái họ không còn thấy.
  const trangThaiLucMo = useRef(lich.status)
  useEffect(() => {
    if (trangThaiLucMo.current !== lich.status) {
      trangThaiLucMo.current = lich.status
      setHoi(null)
    }
  }, [lich.status])
  // K10: giữ chỗ của khách Mini App không có nút "Đã đặt" (chờ khách chốt); K4: lịch đang làm có "Chưa bắt đầu" + "Khách về giữa chừng".
  const cacViec = (daMat ? [] : hanhDongCua(lich.status, lich.source))
    .filter(
      // Huỷ và duyệt lịch làm được cho mọi ngày; các bước "khách đã tới / đang làm" chỉ từ ngày hẹn.
      (h) => ngay <= ngayVn() || h.to === 'cancelled' || h.to === 'nhan' || h.to === 'tu-choi',
    )
    // BK-3a: "Không đến" ẩn cho tới ĐÚNG giờ hẹn (ở cả Lịch lẫn Danh sách — cùng tờ chi tiết). Máy chủ cũng chặn (400).
    .filter((h) => h.to !== 'no_show' || duocBamKhongDen(lich, ngay, ngayVn(), phutBayGio()))
    .filter((h) => choQuay || (BUOC_CUA_THO[lich.status] ?? []).includes(h.to))
    // L-N11 (ADR-078): thợ chỉ lùi "Chưa bắt đầu" khi MỌI dòng của lịch là của mình — lịch nhiều thợ chỉ quầy lùi (máy chủ cũng chặn 403).
    .filter(
      (h) =>
        choQuay ||
        !(lich.status === 'in_service' && h.to === 'checked_in' && lich.nhieuTho === true),
    )
    // T3-N1 (0098): lịch đã từng "Đang làm" (lùi "Chưa bắt đầu" rồi) không còn "Chưa tới" — làm dở thì "Xong" hoặc "Khách về giữa chừng".
    // Máy chủ cũng chặn (400) và DB (SP010).
    .filter((h) => !(lich.status === 'checked_in' && h.to === 'booked' && lich.daTungLam === true))
  const chinh = cacViec.find((h) => h.kind === 'primary')
  const phu = cacViec.filter((h) => h.kind !== 'primary')
  /** KTV đã bấm "Xong" nhưng quầy chưa thu: nút chính là "Thu tiền" (Pha C). */
  const thuSau = lich.status === 'done' && lich.daThu === false && onHoanTat !== undefined

  async function lam(to: string) {
    if (to === 'done' && onHoanTat !== undefined) {
      onHoanTat()
      return
    }
    setDang(true)
    setLoi(null)
    try {
      // Câu "khách được báo bằng cách nào" chỉ có sau khi tiệm HUỶ / TỪ CHỐI (máy chủ trả nhãn — K1a).
      let thongBao: ToastQuay | undefined
      if (to === 'nhan') await api.duyet(lich.appointmentId, true)
      else if (to === 'tu-choi') {
        const kq = await api.duyet(lich.appointmentId, false, lyDo.trim())
        thongBao = {
          chu: toastSauTuChoi(tenKhach(lich.customerName), kq.baoKhach),
          baoKhach: kq.baoKhach,
        }
      }
      // `tu` = trạng thái người bấm đang THẤY: máy khác đã đổi thì máy chủ trả 409, không ghi đè (V4).
      else if (to === 'cancelled') {
        const kq = await api.setStatus(lich.appointmentId, to, lyDo.trim(), lich.status)
        thongBao = {
          chu: toastSauHuy(tenKhach(lich.customerName), kq.baoKhach),
          baoKhach: kq.baoKhach,
        }
      } else await api.setStatus(lich.appointmentId, to, undefined, lich.status)
      if (to === 'checked_in') onDaToi(lich)
      onDaDoi(thongBao)
    } catch (e) {
      setLoi(e instanceof ApiFailure ? e.messageVi : T.statusFailed)
      setHoi(null)
    } finally {
      setDang(false)
    }
  }

  const ten = tenKhach(lich.customerName)
  // Cùng nhãn "Trễ …" / "Chưa tới" như khối lịch và Danh sách (ui-critic v45).
  const chuTre = chuNhanTre(lich, ngay === ngayVn() ? phutBayGio() : null)
  return (
    <To nhan={T.detailTitle} onDong={onDong}>
      <header className="to-dau">
        <h2>{T.detailTitle}</h2>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        <div className="dau-chi-tiet">
          <Avatar ten={ten} co="lon" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <b>{ten}</b>
            <span className={`vien-tt tt-${lich.status}`}>
              {TRANG_THAI[lich.status] ?? lich.status}
            </span>
            {chuTre !== null && <span className="nhan-tre">{chuTre}</span>}
            <DauTrangThaiLich
              soLanDoi={lich.soLanDoiGio}
              gioCu={lich.doiGioTu}
              daXacNhan={lich.daXacNhan}
              chuaXacNhan={lich.chuaXacNhan}
            />
          </div>
        </div>

        {daMat && <ThongBao loai="canh-bao">{T.lichDaDoiMayKhac}</ThongBao>}
        {coc !== undefined && (
          <MucCoc
            api={api}
            lich={lich}
            soTienMacDinh={coc.soTienMacDinh}
            choNhan={coc.choNhan}
            choHoan={coc.choHoan}
            onDaDoi={() => onDaDoi()}
            onLoi={setLoi}
          />
        )}
        {(lich.loiNhan ?? []).length > 0 && <LoiNhanKhach ds={lich.loiNhan ?? []} />}
        {hoSo !== undefined && lich.customerId != null && (
          // Báo / lỗi của khối ảnh + ghi chú nằm NGAY TRONG khối (HoSoBuoi.tsx `PhanAnhBuoi`), không đẩy xuống cuối thân tờ (ngoài màn).
          <HoSoBuoi api={api} lich={lich} coAnh={hoSo.coAnh} />
        )}

        <div className="bang-thong-tin">
          <div>
            <Clock3 size={18} />
            {T.detailTime}
            <span className="gia-tri">
              {gioPhut(lich.startMin)} – {gioPhut(lich.endMin)}
              {/* Từ danh sách nhiều ngày: giờ không kèm ngày là dễ hiểu sai (ui-critic vòng 7). */}
              {/* G-N2: tờ mở ở NGÀY KHÁC hôm nay (nút "Xem lịch cũ nhất" của dải K9, danh sách nhiều ngày) phải nói ngày — giờ trơn dễ hiểu là hôm nay. */}
              {(onXemTrenLich !== undefined || ngay !== ngayVn()) && (
                <span className="gia-tri-phu">{nhanNgayDayDu(ngay)}</span>
              )}
            </span>
          </div>
          <div>
            <Scissors size={18} />
            {T.detailService}
            <span className="gia-tri">{tenDichVu(lich.serviceName)}</span>
          </div>
          <div>
            <UserRound size={18} />
            {T.detailStaff}
            <span className="gia-tri">{tenKtv}</span>
          </div>
          {lich.priceVnd !== null && (
            <div>
              <Wallet size={18} />
              {T.detailPrice}
              <span className="gia-tri">{tienVnd(lich.priceVnd)}</span>
            </div>
          )}
          <div>
            <Phone size={18} />
            {T.detailPhone}
            <span className="gia-tri">
              {lich.customerPhone === null ? (
                T.detailNoPhone
              ) : (
                <a
                  className="lien-ket"
                  href={`tel:${lich.customerPhone}`}
                  aria-label={T.callCustomer}
                >
                  {hienSdt(lich.customerPhone)}
                </a>
              )}
            </span>
          </div>
          <div>
            <Hash size={18} />
            {T.detailCode}
            <span className="gia-tri">{lich.code}</span>
          </div>
        </div>

        {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
        {/* K10: giữ chỗ của khách Mini App — quầy không chốt hộ, chờ khách xác nhận hoặc tự nhả sau 7 phút. */}
        {!daMat && hoi === null && lich.status === 'draft' && lich.source === 'miniapp' && (
          <ThongBao loai="canh-bao">{T.draftMiniApp}</ThongBao>
        )}

        {hoi !== null ? (
          <>
            <ThongBao loai="canh-bao">
              {hoi === 'cancelled'
                ? lich.status === 'in_service'
                  ? // Câu về tiền cọc chỉ khi tờ CÓ mục Tiền cọc và lịch đang giữ cọc (tiệm không thu cọc thì không nhắc tới thứ không thấy trên tờ).
                    coc !== undefined && tinhViecCoc(lich, coc.choNhan, coc.choHoan).dangGiu > 0n
                    ? `${T.confirmLeftMidway} ${T.confirmLeftMidwayCoc}`
                    : T.confirmLeftMidway
                  : T.confirmCancel
                : hoi === 'tu-choi'
                  ? T.confirmReject
                  : T.confirmNoShow}
            </ThongBao>
            {canLyDo && (
              <>
                <OLyDo
                  giaTri={lyDo}
                  onDoi={setLyDo}
                  goiY={T.cancelReasonPlaceholder}
                  ghiChu={hoi === 'tu-choi' ? T.cancelReasonSeen : T.cancelReasonHint}
                />
                {/* "Khách về giữa chừng": lý do đã điền sẵn — ba chip "báo bận / thợ nghỉ / kín giờ" là lý do của lịch CHƯA làm, không hợp. */}
                {lich.status !== 'in_service' && (
                  <div className="hang-chip">
                    {T.cancelReasonChips.map((c) => (
                      <button
                        key={c}
                        type="button"
                        className={`chip ${lyDo === c ? 'dang-chon' : ''}`}
                        onClick={() => setLyDo(c)}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          (phu.length > 0 ||
            onDoiGio !== null ||
            onXemTrenLich !== undefined ||
            onGoiVi !== undefined) && (
            <div className="muc-to">
              <h3>{T.moreActions}</h3>
              <div className="hang-phu">
                {onGoiVi !== undefined && (
                  <>
                    <button type="button" className="nut nut-vien" onClick={onGoiVi}>
                      <Wallet size={18} />
                      {coGoiVi ? G.openKhach : G.openKhachHoSo}
                    </button>
                    <p className="chu-phu">{coGoiVi ? G.openKhachHint : G.openKhachHoSoHint}</p>
                  </>
                )}
                {onXemTrenLich !== undefined && (
                  <button type="button" className="nut nut-vien" onClick={onXemTrenLich}>
                    <CalendarDays size={18} />
                    {T.viewOnCalendar}
                  </button>
                )}
                {onDoiGio !== null && (
                  <button type="button" className="nut nut-vien" onClick={onDoiGio}>
                    <CalendarClock size={18} />
                    {T.reschedule}
                  </button>
                )}
                {phu.map((h) => (
                  <button
                    key={h.to}
                    type="button"
                    className={h.to === 'cancelled' ? 'nut nut-nguy' : 'nut nut-vien'}
                    disabled={dang}
                    onClick={() => {
                      // Việc không phá huỷ (lùi về "đã đặt") làm ngay; huỷ / không đến / từ chối
                      // phải hỏi lại.
                      if (h.to === 'cancelled' || h.to === 'no_show' || h.to === 'tu-choi') {
                        // "Khách về giữa chừng": lý do điền sẵn (sửa được) — quầy chỉ cần xác nhận một chạm.
                        setLyDo(
                          h.to === 'cancelled' && lich.status === 'in_service'
                            ? T.actionLeftMidway
                            : '',
                        )
                        setHoi(h.to)
                      } else void lam(h.to)
                    }}
                  >
                    {h.label}
                  </button>
                ))}
                {/* Nút chữ dài để CUỐI, cả hàng (ui-critic v24): "Không đến | Huỷ lịch" chung một hàng ở trên.
                    Khách trả lời "vẫn đến" trong chat Zalo (không bấm link): quầy ghi thay (N1). */}
                {lich.status === 'booked' && lich.daXacNhan !== true && onDoiGio !== null && (
                  <button
                    type="button"
                    className="nut nut-vien nut-ca-hang"
                    disabled={dang}
                    onClick={() => {
                      setDang(true)
                      void api
                        .khachXacNhan(lich.appointmentId)
                        .then(() => onDaDoi())
                        .catch((e: unknown) =>
                          setLoi(e instanceof ApiFailure ? e.messageVi : T.statusFailed),
                        )
                        .finally(() => setDang(false))
                    }}
                  >
                    {T.markConfirmed}
                  </button>
                )}
              </div>
            </div>
          )
        )}
        {chinh === undefined &&
          hoi === null &&
          !thuSau &&
          !daMat &&
          // Giữ chỗ của khách Zalo đã có dòng riêng ở trên ("Khách đang giữ chỗ…") — không nói thêm "đã xong, không còn việc".
          !(lich.status === 'draft' && lich.source === 'miniapp') &&
          // Mục Tiền cọc còn nút bấm thì không nói "không còn việc gì phải làm" ngay trên nó.
          !(coc !== undefined && tinhViecCoc(lich, coc.choNhan, coc.choHoan).coViec) && (
            <p className="chu-phu">{ngay > ngayVn() ? T.notYetDay : T.noNextStep}</p>
          )}
        {thuSau && hoi === null && <ThongBao loai="canh-bao">{G.notPaidYet}</ThongBao>}
      </div>

      <footer className="to-chan">
        {hoi !== null ? (
          <>
            <button type="button" className="nut nut-vien" onClick={() => setHoi(null)}>
              {T.keepIt}
            </button>
            <button
              type="button"
              className="nut nut-nguy-dac"
              style={{ flex: 1 }}
              disabled={dang || (canLyDo && lyDo.trim().length < 2)}
              onClick={() => void lam(hoi)}
            >
              {hoi === 'cancelled'
                ? lich.status === 'in_service'
                  ? T.confirmLeftMidwayYes
                  : T.confirmCancelYes
                : hoi === 'tu-choi'
                  ? T.confirmRejectYes
                  : T.confirmNoShowYes}
            </button>
          </>
        ) : chinh !== undefined ? (
          <button
            type="button"
            className="nut nut-chinh nut-lon"
            disabled={dang}
            onClick={() => void lam(chinh.to)}
          >
            {chinh.label}
          </button>
        ) : thuSau && onHoanTat !== undefined ? (
          <button type="button" className="nut nut-chinh nut-lon" onClick={onHoanTat}>
            {G.collectNow}
          </button>
        ) : (
          <button type="button" className="nut nut-vien nut-rong" onClick={onDong}>
            {T.close}
          </button>
        )}
      </footer>
    </To>
  )
}

/** Dấu nhỏ trên ô lịch: khách có nhắn tiệm — quầy biết mà mở chi tiết (phản ánh 25/09). */
function DauLoiNhan() {
  return (
    <span
      className="dau-loi-nhan"
      title={T.hasCustomerMessage}
      role="img"
      aria-label={T.hasCustomerMessage}
    >
      <MessageSquareText size={14} strokeWidth={2.2} />
    </span>
  )
}

/**
 * Lời khách nhắn trong tờ chi tiết lịch. Mới nhất trước; gọn thì 2 lời, mỗi lời 3 dòng — để nút
 * "Khách đã tới" không bị đẩy xuống đáy tờ khi khách nhắn dài (phản ánh 25/09). "Xem đủ" mở hết.
 */
function LoiNhanKhach({ ds }: { ds: string[] }) {
  const [moHet, setMoHet] = useState(false)
  const moiTruoc = [...ds].reverse()
  const coGiau = ds.length > 2 || ds.some((m) => m.length > 120 || m.split('\n').length > 3)
  const hien = moHet ? moiTruoc : moiTruoc.slice(0, 2)
  return (
    <div className="loi-nhan-khach">
      <b>{T.customerMessages}</b>
      {hien.map((m, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: lời nhắn chỉ thêm, không đổi thứ tự.
        <p key={i} className={moHet ? undefined : 'kep-3-dong'}>
          {m}
        </p>
      ))}
      {coGiau && (
        <button type="button" className="nut-lien-ket" onClick={() => setMoHet((x) => !x)}>
          {moHet ? T.messagesCollapse : T.messagesShowAll(ds.length)}
        </button>
      )}
    </div>
  )
}
